import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '@verlo/shared/components/ui/input';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import { modifiersApi } from '@verlo/shared/services/api';
import { useApp } from '@verlo/shared/contexts/AppContext';
import { useSite } from '../../../contexts/SiteContext';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import { PageHeader, Stat, Stats, DataTable, FilterBar } from '../../../components/office';

/**
 * Reports › Money › Typed extras (modifiers plan, 21 Sep 2026). The end-of-day report
 * the managers asked for before priced free text could be switched on: every free-text
 * modifier that carried a price, whatever the amount — when, the order, the item, the
 * words, the price, who rang it, who authorised it over the cap.
 */
const iso = (d) => d.toISOString().slice(0, 10);
const when = (s) => { try { return new Date(s).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return s; } };

const TypedExtrasPage = () => {
  const { formatCurrency } = useApp();
  const { site } = useSite();
  const [date, setDate] = useState(iso(new Date()));
  const [days, setDays] = useState('1');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    modifiersApi.typedExtras({ date, days: parseInt(days, 10), ...(site ? { site } : {}) })
      .then(({ data: d }) => { if (live) setData(d); })
      .catch(() => { if (live) setData(null); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [date, days, site]);

  const rows = data?.rows || [];
  const byServer = useMemo(() => {
    const m = {};
    for (const r of rows) { const k = r.rung_by || '—'; m[k] = (m[k] || 0) + r.line_value; }
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  const columns = [
    { key: 'when', label: 'When', sortable: true, render: (r) => <span className="whitespace-nowrap text-ink-soft">{when(r.when)}</span> },
    { key: 'item', label: 'Item', render: (r) => <span className="font-medium text-ink">{r.item}</span> },
    { key: 'text', label: 'Typed', render: (r) => <span className="text-ink">“{r.text}”</span> },
    { key: 'price', label: 'Price', align: 'right', mono: true, sortable: true, render: (r) => <span className={r.price > (data?.cap ?? 5) ? 'font-semibold text-signal-warn' : ''}>+{formatCurrency(r.price)}{r.quantity > 1 ? <span className="text-ink-faint"> ×{r.quantity}</span> : null}</span> },
    { key: 'table', label: 'Order', render: (r) => <span className="text-ink-soft">{r.table || 'bar'} <span className="text-ink-faint">· {r.status}</span></span> },
    { key: 'rung_by', label: 'Rung by', render: (r) => <span className="text-ink-soft">{r.rung_by || '—'}</span> },
    { key: 'authorised_by', label: 'Authorised', render: (r) => r.authorised_by ? <span className="text-ink-soft">{r.authorised_self ? 'own manager rights' : 'manager PIN'}</span> : <span className="text-ink-faint">under the cap</span> },
  ];

  return (
    <ReportsLayout>
      <PageHeader title="Typed extras" purpose="Every free-text modifier that carried a price. Whatever the amount, it's here." testId="typed-extras-header" />
      <FilterBar right={<span>{rows.length} entr{rows.length === 1 ? 'y' : 'ies'}</span>}>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-44 bg-white" data-testid="typed-extras-date" />
        <Segmented value={days} onChange={setDays} options={[{ value: '1', label: 'The day' }, { value: '7', label: 'The week to it' }, { value: '28', label: '4 weeks' }]} />
      </FilterBar>
      {data && !data.enabled && (
        <Notice tone="info" word="Off" title="Priced free text is off for this site" className="mb-4" data-testid="typed-extras-off">
          Typed entries land as £0 notes. A manager can switch pricing on in <Link to="/back-office/settings" className="text-verlo-hover underline">Company › Tills</Link>; this report is what makes that safe.
        </Notice>
      )}
      <Stats className="mb-6" data-testid="typed-extras-tiles">
        <Stat label="Entries" value={data?.count ?? '—'} testId="typed-extras-count" />
        <Stat label="Value" value={formatCurrency(data?.value)} testId="typed-extras-value" />
        <Stat label="Over the cap" value={data?.over_cap ?? '—'} delta={data?.over_cap ? { text: `over ${formatCurrency(data.cap)} · manager PIN`, tone: 'warn' } : undefined} hint={!data?.over_cap && data ? `cap ${formatCurrency(data.cap)}` : undefined} testId="typed-extras-over" />
        <Stat label="Most by" value={byServer[0]?.[0] || '—'} hint={byServer[0] ? formatCurrency(byServer[0][1]) : undefined} />
      </Stats>
      <DataTable columns={columns} rows={rows} rowKey={(r) => `${r.order_id}-${r.audit_id || r.text}`} loading={loading} defaultSort={{ key: 'when', dir: 'desc' }}
        empty={{ word: 'Typed extras', title: 'Nothing priced by hand', children: 'Every typed modifier in this period was a £0 note.' }} testId="typed-extras-table" />
    </ReportsLayout>
  );
};

export default TypedExtrasPage;
